import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2, Send, ShieldCheck, Check, ExternalLink } from "lucide-react";
import { submitReview } from "@/lib/api";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import { IMAGES, GOOGLE_REVIEW_URL } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function LeaveReview() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const prefilledName = params.get("name") || "";
  const prefilledDate = params.get("date") || "";
  const prefilledRating = Number(params.get("rating") || 0);

  const [form, setForm] = useState({
    author_name: prefilledName,
    author_email: "",
    rating: prefilledRating >= 1 && prefilledRating <= 5 ? prefilledRating : 0,
    quote: "",
    tour_date: prefilledDate,
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [wantsToWrite, setWantsToWrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isFromEmail = Boolean(prefilledName || prefilledDate);
  const firstName = prefilledName ? prefilledName.split(" ")[0] : "";
  const ratingChosen = form.rating > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (form.rating < 1) {
      toast.error("Please pick a star rating first");
      return;
    }
    if (!form.author_name.trim() || form.author_name.trim().length < 2) {
      toast.error("Please add your name (or a nickname)");
      return;
    }
    if (wantsToWrite && form.quote.trim().length > 0 && form.quote.trim().length < 20) {
      toast.error("Written review needs to be at least 20 characters, or leave it blank");
      return;
    }
    setSubmitting(true);
    try {
      await submitReview({
        author_name: form.author_name.trim(),
        author_email: form.author_email || null,
        rating: form.rating,
        quote: wantsToWrite && form.quote.trim() ? form.quote.trim() : null,
        tour_date: form.tour_date || null,
      });
      toast.success(
        wantsToWrite && form.quote.trim()
          ? "Thanks — Martin will read your review personally."
          : `Thanks for the ${form.rating}-star rating!`
      );
      setTimeout(() => navigate("/"), 1400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="leave-review-page">
      {/* HERO */}
      <section className="relative h-[36vh] min-h-[300px] overflow-hidden">
        <img src={IMAGES.parliament} alt="Parliament House" className="absolute inset-0 w-full h-full object-cover sepia-soft" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-10">
          <div className="stamp bg-[#FDFBF7]/85 w-fit">
            {isFromEmail ? "Thank you for walking with Martin" : "Just finished the walk?"}
          </div>
          <h1 className="serif text-white text-5xl md:text-6xl mt-4">
            {firstName ? `Great to hear from you, ${firstName}.` : "How was the walk?"}
          </h1>
          <p className="text-[#F5EFEB] mt-3 max-w-2xl">
            Rate Martin out of 5 stars — takes 5 seconds. If you'd like to leave a written review too, that's a bonus.
          </p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-6 lg:px-10 py-16">
        <form onSubmit={submit} className="card-warm rounded-2xl p-8 md:p-10" data-testid="review-form">
          {/* STEP 1: RATING */}
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">Step 1 · Your rating</div>
            <h2 className="serif text-2xl md:text-3xl text-[#6E2638] mt-2">
              Rate Martin out of 5 stars
            </h2>
            <div className="flex justify-center gap-2 mt-6" onMouseLeave={() => setHoverRating(0)}>
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hoverRating || form.rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => update("rating", n)}
                    onMouseEnter={() => setHoverRating(n)}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    data-testid={`star-${n}`}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      size={44}
                      className={filled ? "text-[#D4AF37]" : "text-[#E5D9C5]"}
                      fill={filled ? "#D4AF37" : "none"}
                      strokeWidth={1.5}
                    />
                  </button>
                );
              })}
            </div>
            {form.rating > 0 && (
              <div className="text-sm text-[#6E2638] font-semibold mt-3">
                {["", "One star", "Two stars", "Three stars", "Four stars", "Five stars"][form.rating]}
              </div>
            )}
          </div>

          {/* NAME (always required once rating is chosen) */}
          {ratingChosen && (
            <div className="mt-8 animate-in fade-in duration-500">
              <OrnamentalDivider label="A couple of details" />
              <div className="space-y-4">
                <div>
                  <Label htmlFor="review-name">Your name *</Label>
                  <Input id="review-name" data-testid="input-review-name"
                    value={form.author_name}
                    onChange={(e) => update("author_name", e.target.value)}
                    placeholder="e.g. Sophie H."
                    maxLength={60}
                    required />
                  <p className="text-xs text-[#1A1817]/60 mt-1">
                    First name + last initial is our default — nickname works too.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="review-date">Tour date (optional)</Label>
                    <Input id="review-date" type="date" data-testid="input-review-date"
                      value={form.tour_date}
                      onChange={(e) => update("tour_date", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="review-email">Email (private, optional)</Label>
                    <Input id="review-email" type="email" data-testid="input-review-email"
                      value={form.author_email}
                      onChange={(e) => update("author_email", e.target.value)}
                      placeholder="you@example.com" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: OPTIONAL WRITTEN REVIEW */}
          {ratingChosen && !wantsToWrite && (
            <div className="mt-8 rounded-2xl bg-[#F5EFEB] border border-[#E5D9C5] p-5 text-center animate-in fade-in duration-500" data-testid="offer-write-review">
              <div className="serif text-lg text-[#6E2638]">Want to leave a written review too?</div>
              <p className="text-sm text-[#1A1817]/75 mt-1">
                Totally optional — but it'd help other Melbourne-curious folks decide.
              </p>
              <button
                type="button"
                onClick={() => setWantsToWrite(true)}
                className="btn-outline-gold mt-4"
                data-testid="add-written-review"
              >
                Yes, write a review
              </button>
            </div>
          )}

          {ratingChosen && wantsToWrite && (
            <div className="mt-8 animate-in fade-in duration-500" data-testid="written-review-block">
              <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">Step 2 · Your review (optional)</div>
              <Label htmlFor="review-quote" className="mt-2 block">Tell others what you loved</Label>
              <Textarea id="review-quote" data-testid="input-review-quote"
                rows={5}
                minLength={20}
                maxLength={800}
                placeholder="What surprised you? What'll you remember? Would you recommend it?"
                value={form.quote}
                onChange={(e) => update("quote", e.target.value)} />
              <p className="text-xs text-[#1A1817]/60 mt-1">
                {form.quote.length}/800 characters · minimum 20 if writing one
              </p>

              <div className="mt-4 rounded-xl bg-[#F5EFEB] border border-[#E5D9C5] p-4 flex items-start gap-3">
                <ShieldCheck size={20} className="text-[#1E4637] shrink-0 mt-0.5" />
                <p className="text-xs text-[#1A1817]/85 leading-relaxed">
                  Martin reads reviews before they appear on the site — usually within a day. We never edit content, only choose to display or hide.
                </p>
              </div>
            </div>
          )}

          {ratingChosen && (
            <button type="submit" disabled={submitting} className="btn-primary mt-8 w-full justify-center" data-testid="submit-review">
              {submitting ? (
                <><Loader2 className="animate-spin" size={18} /> Sending…</>
              ) : wantsToWrite && form.quote.trim().length >= 20 ? (
                <>Send my rating &amp; review <Send size={16} /></>
              ) : (
                <>Send my rating <Check size={16} /></>
              )}
            </button>
          )}

          {/* GOOGLE REVIEW CTA — only for 4-5 star ratings AND once Martin's Google Business URL is live */}
          {ratingChosen && form.rating >= 4 && GOOGLE_REVIEW_URL && (
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center justify-center gap-2 w-full rounded-full border-2 border-[#4285F4] text-[#4285F4] px-6 py-3 font-semibold hover:bg-[#4285F4] hover:text-white transition-colors"
              data-testid="google-review-cta"
            >
              Also share this on Google <ExternalLink size={16} />
            </a>
          )}

          <div className="text-center mt-6 text-xs text-[#1A1817]/60">
            Not ready? <Link to="/" className="text-[#6E2638] underline">Back to home</Link>
          </div>
        </form>
      </section>
    </div>
  );
}
